import { useEffect, useState } from 'react'
import NameIcon from './shared/NameIcon'
import Spinner from 'app/components/Spinner'
import { gqlQuery } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { debug, logWarn } from 'lib/logging'
import { graphql } from 'app/generated/gql'

let userFetching: Record<string, Promise<string>> = {}
let userCache: Record<string, string> = {}
let staffFetching: Record<string, Promise<string>> = {}
let staffCache: Record<string, string> = {}

/**
 * Fetches a user's display name via GraphQL (legacy users-table query).
 * Module-scope cache so repeated id lookups are free.
 */
// Query only the fields we need for display. We deliberately bypass MODEL_FIELDS['user']
// because that pulls a wide set including `role`, which is non-nullable in the schema and
// currently throws INTERNAL_SERVER_ERROR for some users — that bubbles up and nullifies
// the entire user object. Asking only for {id name email} keeps the response intact.
const USER_NAME_QUERY = graphql(`
  query LazyStaffCellUser($id: ID!) {
    user(id: $id) {
      id
      name
      email
    }
  }
`)

export async function getName(userId: string): Promise<string | undefined> {
  if (userCache[userId] != null) return userCache[userId]
  if (userFetching[userId] != null) return userFetching[userId]
  if (!userId || userId === `undefined`) return
  userFetching[userId] = (async () => {
    try {
      const { data, error } = await gqlQuery(USER_NAME_QUERY, { id: userId })
      if (error || !data?.user) {
        debug(`tasks`, `user(${userId}) returned no data`, { error })
        return (userCache[userId] = `Unknown`)
      }
      const display = data.user.name || data.user.email
      if (!display) {
        debug(`tasks`, `user(${userId}) has no name or email`, data.user)
        return (userCache[userId] = `Unknown`)
      }
      return (userCache[userId] = display)
    } catch (err) {
      logWarn(`user(${userId}) fetch threw`, err)
      return (userCache[userId] = `Unknown`)
    }
  })()
  return userFetching[userId]
}

/**
 * Fetches a staff member's display name via GraphQL (staff table).
 * Use this in staff-v2 contexts. Module-scope cache.
 */
// The schema exposes `staffMember(id)`, not `staff(id)`. The old raw string asked
// for `staff` and every lookup failed, so this cell always rendered `Unknown` for
// a staff id.
const STAFF_NAME_QUERY = graphql(`
  query LazyStaffCellStaff($id: ID!) {
    staffMember(id: $id) {
      id
      name
    }
  }
`)

export async function getStaffName(staffId: string): Promise<string | undefined> {
  if (staffCache[staffId] != null) return staffCache[staffId]
  if (staffFetching[staffId] != null) return staffFetching[staffId]
  if (!staffId || staffId === `undefined` || staffId === `AI`) return
  staffFetching[staffId] = (async () => {
    try {
      const { data, error } = await gqlQuery(STAFF_NAME_QUERY, { id: staffId })
      if (error || !data?.staffMember?.name) {
        debug(`tasks`, `staffMember(${staffId}) returned no name`, { error })
        return (staffCache[staffId] = `Unknown`)
      }
      return (staffCache[staffId] = data.staffMember.name)
    } catch (err) {
      logWarn(`staff(${staffId}) fetch threw`, err)
      return (staffCache[staffId] = `Unknown`)
    }
  })()
  return staffFetching[staffId]
}

export function LazyStaffCell(props: {
  staffId?: string // Preferred: Staff ID for querying Staff table
  userId?: string // Deprecated: for backward compatibility, queries Users table
  loadingIndicator?: boolean
}) {
  const id = props.staffId || props.userId
  const isStaffId = !!props.staffId
  const cache = isStaffId ? staffCache : userCache

  const [name, setName] = useState<string>(id ? cache[id] : ``)

  useEffect(() => {
    if (!id) {
      setName(``)
      return
    }
    if (name && cache[id] === name) return
    else setName(``)

    const fetchFunction = isStaffId ? getStaffName : getName
    bg(
      fetchFunction(id).then((fetched) => {
        if (fetched) setName(fetched)
      }),
    )
  }, [id, isStaffId])

  return name ? (
    <div className="flex items-center gap-x-2">
      <NameIcon
        fullName={name[0]}
        heightClassName="h-5"
        widthClassName="w-5"
        textClassName="text-strong font-bold"
        backgroundClassName="bg-surface-strong"
        borderClassName="border border-strong"
        isTiny
      />
      <span>{name}</span>
    </div>
  ) : props.loadingIndicator !== false ? (
    <Spinner size={5} />
  ) : null
}
